import { useEffect, useId, useState, type FormEvent } from 'react';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { loseDeal, useLostReasons } from '../api';
import type { DealRecord, LoseBody, LostReason } from '../types';
import { FieldBlock, InfoLine } from './bits';
import { NextStepFields, useNextStepForm } from './NextStepFields';
import styles from './dialogs.module.css';

/**
 * Deal-Pipeline 21 „Deal verloren setzen": the seven reasons with their follow-up state, the info line,
 * the note for „sonstiges" and the mandatory next step of a follow-up reason (`POST /deals/:id/lose`).
 */

export interface LoseDialogProps {
  open: boolean;
  dealId: string;
  contactName: string;
  /** „WE 2.01 · 358.000 €" */
  line: string;
  /** The unit that becomes free („WE 2.01 wird frei"), or null. */
  unitNumber: string | null;
  onClose: () => void;
  onDone: (deal: DealRecord, nextStep: 'book_appointment' | null) => void;
}

function infoText(reason: LostReason | null, unitNumber: string | null): string {
  const outcome = reason?.contactOutcome === 'disqualified' ? 'Disqualifiziert' : 'Follow Up';
  const unit = unitNumber ? ` · ${unitNumber} wird frei` : '';
  return `Kontakt geht auf ${outcome}${unit}`;
}

export function LoseDialog({
  open,
  dealId,
  contactName,
  line,
  unitNumber,
  onClose,
  onDone,
}: LoseDialogProps) {
  const reasons = useLostReasons(open);
  const formId = useId();
  const [reasonId, setReasonId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{
    reason?: string | undefined;
    note?: string | undefined;
    nextStep?: string | undefined;
  }>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const nextStep = useNextStepForm('follow_up');

  useEffect(() => {
    if (!open) return;
    setReasonId(null);
    setNote('');
    setErrors({});
    setBanner(null);
    nextStep.reset({ kind: 'follow_up' });
  }, [open, dealId]);

  const items = reasons.data?.items ?? [];
  // Deal-Pipeline 21 preselects „Finanzierung abgelehnt"
  useEffect(() => {
    if (!open || reasonId !== null || items.length === 0) return;
    const preset = items.find((item) => item.name === 'Finanzierung abgelehnt') ?? null;
    if (preset) setReasonId(preset.id);
  }, [open, items, reasonId]);
  const selected = items.find((item) => item.id === reasonId) ?? null;
  const needsNextStep = selected?.contactOutcome === 'follow_up';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const next: typeof errors = {};
    if (!selected) next.reason = 'Bitte wählen Sie einen Grund.';
    if (selected?.noteRequired && note.trim() === '') next.note = 'Bitte geben Sie einen Grund an.';
    const step = needsNextStep ? nextStep.build() : null;
    if (needsNextStep && step === null) next.nextStep = 'Bitte legen Sie den nächsten Schritt fest.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !selected) return;
    const body: LoseBody = { lostReasonId: selected.id };
    if (note.trim() !== '') body.note = note.trim();
    if (step) body.nextStep = step;
    setPending(true);
    setBanner(null);
    try {
      const deal = await loseDeal(dealId, body);
      onDone(deal, step?.kind === 'book_appointment' ? 'book_appointment' : null);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        const fields = error.fields;
        setErrors({ reason: fields['lostReasonId'], note: fields['note'], nextStep: fields['nextStep'] });
        if (Object.keys(fields).length === 0) setBanner(error.message);
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="danger"
      title="Deal verloren setzen"
      subtitle={`${contactName} · ${line}`}
      className={styles.stageDialog}
      initialFocus="container"
      footer={
        <>
          <Button variant="secondary" className={styles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="destructiveFilled"
            className={styles.footBtn}
            loading={pending}
          >
            Verloren setzen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div>
          <div className={styles.sectionHead}>
            <span className={styles.sectionLabel}>Grund · Pflichtangabe</span>
            <span className={styles.sectionHint}>bestimmt den Folgezustand</span>
          </div>
          {reasons.isPending ? (
            <div className={styles.reasons}>
              <Skeleton variant="block" height={38} />
              <Skeleton variant="block" height={38} />
              <Skeleton variant="block" height={38} />
            </div>
          ) : null}
          {reasons.isError ? <Banner tone="danger">{errorMessage(reasons.error)}</Banner> : null}
          {reasons.data ? (
            <div className={styles.reasons} role="radiogroup" aria-label="Grund">
              {items.map((item) => {
                const active = item.id === reasonId;
                return (
                  <label key={item.id} className={clsx(styles.reason, active && styles.reasonActive)}>
                    <input
                      type="radio"
                      name={`${formId}-reason`}
                      className={styles.reasonInput}
                      value={item.id}
                      checked={active}
                      onChange={() => {
                        setReasonId(item.id);
                        setErrors({});
                      }}
                    />
                    <span className={styles.reasonDot} aria-hidden="true" />
                    <span className={styles.reasonLabel}>{item.name}</span>
                    <span className={styles.reasonOutcome}>{item.outcomeLabel}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
          {errors.reason ? (
            <div className={styles.fieldError} role="alert">
              {errors.reason}
            </div>
          ) : null}
        </div>
        {selected ? <InfoLine>{infoText(selected, unitNumber)}</InfoLine> : null}
        {selected?.noteRequired ? (
          <FieldBlock
            label="Grund als Text"
            note="Pflicht bei „sonstiges“"
            error={errors.note}
            htmlFor={`${formId}-note`}
          >
            <textarea
              id={`${formId}-note`}
              className={clsx(styles.input, styles.textarea, errors.note && styles.inputInvalid)}
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </FieldBlock>
        ) : selected ? (
          <FieldBlock label="Notiz" note="optional" htmlFor={`${formId}-note`}>
            <input
              id={`${formId}-note`}
              className={styles.input}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </FieldBlock>
        ) : null}
        {needsNextStep ? <NextStepFields form={nextStep} error={errors.nextStep} /> : null}
      </form>
    </Dialog>
  );
}
